import React, { useEffect, useState } from 'react';
import { useRouteMatch } from 'react-router';
import { connect, ConnectedProps } from 'react-redux';
import { TFunction } from 'react-i18next';
import { History } from 'history';
import { ConfigService, withInject, Pagination as PaginationManager } from '@piwikpro/platform';
import { ListPreference } from '@piwikpro/user-preferences';
import {
  Text,
  StyledContainer,
  Pagination,
  Tag,
  Layout,
  Stack,
  View,
  withTranslation,
  Button,
  List,
  Spinner,
  formatDate,
  formatToOptions,
  SplashScreen,
  Subtitle,
  Title as TitleComponent,
} from '@piwikpro/ui-components';
import moment from 'moment';
import { BillingService } from '../../BillingService';
import PaymentBadge from '../PaymentBadge/PaymentBadge';
import ScheduledActionInformation from '../ScheduledActionInformation';
import { MarketingCratePaymentsState, Transaction } from '../../redux';

const mapStateToProps = (state: MarketingCratePaymentsState) => ({
  isBillingLoading: state.payments.billing.isLoading,
  isSubscriptionLoading: state.payments.subscription.isLoading,
  failed: state.payments.billing.failed
   || state.payments.subscription.failed,
  hasPaddleSubscription: Boolean(state.payments.subscription.details),
  transactions: state.payments.billing.details,
  total: state.payments.billing.details?.length || 0,
  subscription: state.payments.subscription.details,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  billingService: BillingService
  config: ConfigService
  Title: any
  userPreferences: any
}

interface Props extends ReduxProps, InjectedProps {
  history: History
  i18n: import('i18next').i18n
  t: TFunction
}

const getUrlWithoutParams = (url: string) => url.substring(0, url.indexOf(':'));
function downloadURI(uri: string, name: string) {
  const link = document.createElement('a');
  link.download = name;
  link.href = uri;
  link.target = '_parent';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const Billing: React.FC<Props> = ({
  transactions,
  isBillingLoading,
  isSubscriptionLoading,
  failed,
  config,
  billingService,
  Title,
  total,
  history,
  userPreferences,
  hasPaddleSubscription,
  subscription,
  i18n,
  t,
}) => {
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'dsc'>();
  const [sortedAndSplittedTransactions, setSortedAndSplittedTransactions] = useState(transactions);
  const { params, path, url } = useRouteMatch();
  const pagination = PaginationManager.fromQueryParams(
    transactions,
    total,
    params,
  );

  const fetchTransactions = async () => {
    try {
      await billingService.getTransactions();
      await billingService.getCurrentSubscriptionTier();
      await billingService.getTiers();
    // eslint-disable-next-line no-empty
    } catch {}
  };

  useEffect(() => {
    if (transactions?.length) {
      const transactionsToSort = [...transactions].filter(transaction => !!transaction.invoice_number);
      transactionsToSort?.sort((a, b) => {
        if (dateSortOrder === 'asc') {
          return moment(a.billing_date).valueOf() - moment(b.billing_date).valueOf();
        }

        return moment(b.billing_date).valueOf() - moment(a.billing_date).valueOf();
      });
      const sortedAndPaginatedTransactions = transactionsToSort.slice(
        pagination.offset,
        pagination.offset + pagination.getLimit(),
      );
      setSortedAndSplittedTransactions(sortedAndPaginatedTransactions);
    }
  }, [dateSortOrder, url, transactions]);

  const getListRow = (transaction: Transaction) => (
    <List.Item key={transaction.transaction_id}>
      <Text>{transaction.invoice_number}</Text>
      <Tag color={transaction.status === 'completed' ? 'green' : 'red'}>
        {t(transaction.status === 'completed' ? 'paddle.billing.transaction.payment-status.success' : 'paddle.billing.transaction.payment-status.failed')}
      </Tag>
      <Text>{transaction.tier_name}</Text>
      <Text>
        {formatDate(
          moment(transaction.billing_date),
          i18n.language.split('-')[0],
          formatToOptions('LL LT'),
        )}
      </Text>
      <Button
        text={t('paddle.billing.transaction.invoice-download.btn-text')}
        appearance="simple"
        icon="download"
        onClick={async () => {
          try {
            const invoiceUrl = await billingService.getInvoiceUrl(transaction.transaction_id);
            downloadURI(invoiceUrl, `${transaction.invoice_number}.pdf`);
            // eslint-disable-next-line no-empty
          } catch {}
        }}
      />
    </List.Item>
  );

  const handleLimitChange = async (limit) => {
    const listPreference = new ListPreference(userPreferences.load(), '');
    listPreference.limit = limit;

    userPreferences.persist(listPreference);

    history.push(`${getUrlWithoutParams(path)}1/${limit}`);
  };

  const handlePageChange = async (offset) => {
    history.push(`${getUrlWithoutParams(path)}${pagination.getPageFromOffset(offset)}/${pagination.getLimit()}`);
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  if (isBillingLoading) {
    return <Spinner type="centered" className="x-large" />;
  }

  if (!isSubscriptionLoading && !hasPaddleSubscription) {
    return (
      <SplashScreen type="custom">
        <Stack vertical spacing="extraNarrow" verticalAlignment="center" horizontalAlignment="middle">
          <Stack.Item>
            <TitleComponent>
              {t('paddle.billing.no-subscription.title')}
            </TitleComponent>
          </Stack.Item>
          <Stack.Item>
            <Subtitle>
              {t('paddle.billing.no-subscription.description')}
            </Subtitle>
          </Stack.Item>
        </Stack>
      </SplashScreen>
    );
  }

  if (failed) {
    return <SplashScreen type="internalServerError" />;
  }

  return (
    <Title part={t('platform.billing.title')}>
      <Layout maxWidth="wide">
        <View>
          <View.Header title={t('paddle.billing.view-header.title')} />
          <View.Body>
            <Stack vertical spacing="narrow">
              <Stack.Item fill>
                {
                  Boolean(subscription?.scheduled_change_action) && <ScheduledActionInformation action={subscription?.scheduled_change_action} />
                }
              </Stack.Item>
              <Stack.Item fill>
                <StyledContainer
                  border={['left', 'top', 'right']}
                  padding="mid"
                  backgroundColor="gray"
                >
                  <PaymentBadge hidePlanName />
                </StyledContainer>
              </Stack.Item>
              <Stack.Item fill previousSpacing="none">
                <List
                  columns={[
                    {
                      size: 1,
                      name: t('paddle.billing.transactions-columns.invoice-number'),
                      align: 'left',
                    },
                    {
                      size: 1,
                      name: t('paddle.billing.transactions-columns.payment-status'),
                      align: 'left',
                    },
                    {
                      size: 1,
                      name: t('paddle.billing.transactions-columns.plan'),
                      align: 'left',
                    },
                    {
                      size: 2,
                      name: (
                        <List.SortableHeader
                          text={t('paddle.billing.transactions-columns.billing-date')}
                          order={dateSortOrder}
                          selected={dateSortOrder !== null}
                          onClick={() => setDateSortOrder(dateSortOrder === 'asc' ? 'dsc' : 'asc')}
                        />
                      ),
                      align: 'left',
                    },
                    {
                      size: 1,
                      name: '',
                      align: 'right',
                    },
                  ]}
                >
                  {sortedAndSplittedTransactions?.map(getListRow)}
                </List>
              </Stack.Item>
              <Stack.Item fill>
                <Pagination
                  onLimitChange={handleLimitChange}
                  limit={pagination.getLimit()}
                  total={total}
                  limitOptions={config.get('PAGINATION_ITEMS_PER_PAGE_OPTIONS')}
                  startIndex={pagination.offset}
                  endIndex={pagination.offset + pagination.getLimit()}
                  onPageChange={handlePageChange}
                />
              </Stack.Item>
            </Stack>
          </View.Body>
        </View>
      </Layout>
    </Title>
  );
};

export default connector(withInject<InjectedProps>({
  billingService: 'MarketingCrate.billingService',
  Title: 'TitleCrate.components.Title',
  userPreferences: 'userPreferences',
  config: 'config',
  history: 'RouterCrate.history',
})(withTranslation(['administration'])(Billing as any)));
